import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { API_URL } from "../../utils/config";
import DetallsProveidorProducte from "./Detallsproveidorproducte";
import TeclaEscEnter from "../../utils/Teclaescenter";
import { formatDate, formatNumero } from "../../utils/Utilitats";
import { tincPermis } from "../../utils/Permisos";
import { useUserContext } from "../../utils/UserContext";
import AjudaCataleg from "./AjudaCataleg";
import { BotoAjuda } from "./AjudaModal";

function idPreu(prov) {
  return prov ? Number(prov.idProductesProveidors) : null;
}

function nomProveidor(prov) {
  if (prov.TipusEntitat === "Autonom" || prov.TipusEntitat === "Particular") {
    const cognoms = [prov.Cognom1, prov.Cognom2].filter(Boolean).join(" ");
    return cognoms ? `${cognoms}, ${prov.Nom}` : prov.Nom;
  }
  return prov.Nom;
}

function calcularPreu(prov) {
  const preuSenseOferta = parseFloat(prov.PreuNet) || 0;
  const preuOferta = parseFloat(prov.PreuOferta) || 0;
  return prov.Oferta ? Math.min(preuSenseOferta, preuOferta) : preuSenseOferta;
}

function ProveidorsProducte({ product, onCancel, subnav }) {
  const { rolUsuari } = useUserContext();
  const pucCrear = tincPermis(rolUsuari, "CREAR");
  const pucConsultar = tincPermis(rolUsuari, "CONSULTAR");
  const pucModificar = tincPermis(rolUsuari, "MODIFICAR");
  const pucEsborrar = tincPermis(rolUsuari, "ESBORRAR");

  const [vista, setVista] = useState("llista");
  const [mostraAjuda, setMostraAjuda] = useState(false);
  const [provList, setProvList] = useState([]);
  const [selectedProv, setSelectedProv] = useState(null);
  const [showOnlyActius, setShowOnlyActius] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: "DataAlta", direction: "desc" });
  const [menuObert, setMenuObert] = useState(null);
  const [menuPos, setMenuPos] = useState(null);
  const menuRef = useRef(null);

  TeclaEscEnter(vista === "llista" ? onCancel : () => setVista("llista"), null);

  const fetchProveidorsProducte = useCallback(async () => {
    if (!product?.idProductes) return;
    try {
      const response = await fetch(`${API_URL}/Proveidorsproducte.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idProductes: product.idProductes }),
      });
      const data = await response.json();
      if (data.success) {
        setProvList(
          (data.proveidors || []).map((p) => ({
            ...p,
            Actiu: parseInt(p.Actiu, 10) === 1,
          }))
        );
      } else {
        setProvList([]);
      }
    } catch (error) {
      console.error("Error carregant Proveïdors del producte:", error);
      setProvList([]);
    }
  }, [product]);

  useEffect(() => {
    fetchProveidorsProducte();
    setVista("llista");
    setSelectedProv(null);
    setMenuObert(null);
  }, [fetchProveidorsProducte]);

  useEffect(() => {
    const tancar = (e) => {
      if (e.target.closest(".dv2-menu") || e.target.closest(".dv2-menuBtn")) return;
      setMenuObert(null);
    };
    document.addEventListener("mousedown", tancar);
    return () => document.removeEventListener("mousedown", tancar);
  }, []);

  const tornarLlista = (refresca = false) => {
    setVista("llista");
    setSelectedProv(null);
    setMenuObert(null);
    if (refresca) fetchProveidorsProducte();
  };

  const obrirFitxa = (prov, mode) => {
    setMenuObert(null);
    setSelectedProv(prov);
    setVista(mode);
  };

  const obrirMenu = (e, prov) => {
    e.stopPropagation();
    const id = idPreu(prov);
    if (menuObert === id) {
      setMenuObert(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const ample = 200;
    const alt = 160;
    const marge = 8;
    let top = rect.bottom + 4;
    if (top + alt > window.innerHeight - marge) {
      top = Math.max(marge, rect.top - alt - 4);
    }
    let left = rect.right - ample;
    if (left < marge) left = marge;
    if (left + ample > window.innerWidth - marge) {
      left = window.innerWidth - ample - marge;
    }
    setMenuPos({ top, left });
    setMenuObert(id);
    setSelectedProv(prov);
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const filtered = (showOnlyActius ? provList.filter((p) => p.Actiu) : provList.filter((p) => !p.Actiu)).slice();
  filtered.sort((a, b) => {
    let valA;
    let valB;
    switch (sortConfig.key) {
      case "Preu":
        valA = calcularPreu(a);
        valB = calcularPreu(b);
        break;
      case "PreuPVPUnitari":
        valA = parseFloat(a.PreuPVPUnitari) || 0;
        valB = parseFloat(b.PreuPVPUnitari) || 0;
        break;
      case "DteProveidor":
        valA = parseFloat(a.DteProveidor) || 0;
        valB = parseFloat(b.DteProveidor) || 0;
        break;
      case "DataAltaOriginal":
      case "DataAlta":
      case "DataBaixa":
        valA = new Date(a[sortConfig.key] || "").getTime();
        valB = new Date(b[sortConfig.key] || "").getTime();
        break;
      default:
        valA = (nomProveidor(a) || "").toLowerCase();
        valB = (nomProveidor(b) || "").toLowerCase();
    }
    if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
    if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  const th = (key, label, extra = "") => (
    <th className={`${extra} ${sortConfig.key === key ? "is-sorted" : ""}`} onClick={() => handleSort(key)}>
      {label} {sortConfig.key === key ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
    </th>
  );

  const preuMenu = menuObert ? filtered.find((p) => idPreu(p) === menuObert) : null;

  if (vista !== "llista") {
    return (
      <DetallsProveidorProducte
        mode={vista}
        producte={product}
        proveidorProd={vista === "nou" ? null : selectedProv}
        subnav={subnav}
        onCancel={() => tornarLlista(false)}
        onSave={() => tornarLlista(true)}
        onCanviarMode={(modeNou) => setVista(modeNou)}
      />
    );
  }

  return (
    <div className="dv2-entity">
      {mostraAjuda && <AjudaCataleg pestanya="preus" onTancar={() => setMostraAjuda(false)} />}
      <div className="dv2-pageHeader">
        <div className="dv2-pageHeader-main">
          <div className="dv2-pageHeader-icon" aria-hidden="true">
            €
          </div>
          <div>
            <h1 className="dv2-pageHeader-title">Preus proveïdors</h1>
            <p className="dv2-pageHeader-subtitle">
              {[product?.Marca, product?.Model].filter(Boolean).join(" ") || "Tarifes del producte"}
            </p>
          </div>
        </div>
        <div className="dv2-pageHeader-actions">
          <BotoAjuda title="Com funcionen els Preus de proveïdor" onClick={() => setMostraAjuda(true)} />
          <button type="button" className="dv2-btn" onClick={onCancel}>
            Tornar
          </button>
          {pucCrear && (
            <button
              type="button"
              className="dv2-btn dv2-btn--primary"
              onClick={() => {
                setSelectedProv(null);
                setVista("nou");
              }}
            >
              Nou preu
            </button>
          )}
        </div>
      </div>
      {subnav}

      <div className="dv2-entity-body">
        <div className="dv2-filterBar">
          <label className="dv2-filter dv2-filter--toggle">
            <span className="dv2-filter-label">Mostrar actius</span>
            <span className="dv2-toggleRow">
              <span className="dv2-toggle">
                <input type="checkbox" checked={showOnlyActius} onChange={() => setShowOnlyActius(!showOnlyActius)} />
                <span className="dv2-toggle-track" />
              </span>
              {showOnlyActius ? "Actius" : "Inactius"}
            </span>
          </label>
        </div>

        {filtered.length === 0 ? (
          <p className="dv2-empty">No hi ha preus {showOnlyActius ? "actius" : "inactius"} per aquest producte.</p>
        ) : (
          <div className="dv2-tableWrap">
            <table className="dv2-table">
              <thead>
                <tr>
                  {th("DataAltaOriginal", "Data alta")}
                  {th("DataAlta", "Última modif.")}
                  {!showOnlyActius && th("DataBaixa", "Data baixa")}
                  {th("Nom", "Proveïdor")}
                  {th("PreuPVPUnitari", "PVP", "dv2-num")}
                  {th("DteProveidor", "Dte. %", "dv2-num")}
                  {th("Preu", "Preu net", "dv2-num")}
                  <th className="dv2-table-actions">Accions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((prov) => (
                  <tr
                    key={prov.idProductesProveidors}
                    className={selectedProv?.idProductesProveidors === prov.idProductesProveidors ? "is-selected" : ""}
                    onClick={() =>
                      setSelectedProv((prev) =>
                        prev?.idProductesProveidors === prov.idProductesProveidors ? null : prov
                      )
                    }
                    onDoubleClick={() => pucConsultar && obrirFitxa(prov, "consultar")}
                  >
                    <td>{formatDate(prov.DataAltaOriginal) || "—"}</td>
                    <td>{formatDate(prov.DataAlta) || "—"}</td>
                    {!showOnlyActius && <td>{formatDate(prov.DataBaixa) || "—"}</td>}
                    <td>{nomProveidor(prov) || "—"}</td>
                    <td className="dv2-num">{formatNumero(prov.PreuPVPUnitari)}</td>
                    <td className="dv2-num">{formatNumero(prov.DteProveidor)}</td>
                    <td className="dv2-num">
                      {formatNumero(calcularPreu(prov))}
                      {prov.Oferta ? " · oferta" : ""}
                    </td>
                    <td className="dv2-table-actions">
                      <div className="dv2-table-actions">
                        <button
                          type="button"
                          className={`dv2-menuBtn ${menuObert === idPreu(prov) ? "is-open" : ""}`}
                          aria-label="Accions"
                          onClick={(e) => obrirMenu(e, prov)}
                        >
                          …
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {preuMenu &&
        menuPos &&
        createPortal(
          <div className="dv2-menu dv2-menu--fixed" ref={menuRef} style={{ top: menuPos.top, left: menuPos.left }}>
            {pucConsultar && (
              <button type="button" className="dv2-menu-item" onClick={() => obrirFitxa(preuMenu, "consultar")}>
                Consultar
              </button>
            )}
            {pucModificar && (
              <button type="button" className="dv2-menu-item" onClick={() => obrirFitxa(preuMenu, "modificar")}>
                Modificar
              </button>
            )}
            {pucEsborrar && (
              <button type="button" className="dv2-menu-item is-danger" onClick={() => obrirFitxa(preuMenu, "esborrar")}>
                Desactivar
              </button>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}

export default ProveidorsProducte;
